import { classifyLdaDeliverable } from './affairLdaClassifier';
const strip = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
// Canonical text normalizer used by matching and dependency-section keys.
const normalizeText = (value) => strip(value);
const asText = (value) => {
  if (value == null) return '';
  if (value instanceof Date) return value;
  if (typeof value === 'object') {
    if ('result' in value && value.result != null) return value.result;
    if ('text' in value && value.text != null) return value.text;
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text || '').join('');
  }
  return value;
};
const parseDelimitedLine = (line, delimiter) => {
  const values = []; let value = ''; let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') { value += '"'; i += 1; }
      else quoted = !quoted;
    } else if (char === delimiter && !quoted) { values.push(value); value = ''; }
    else value += char;
  }
  values.push(value); return values;
};
const FIELD_ALIASES = {
  reference: ['reference', 'ref', 'n document', 'no document', 'numero document', 'numero', 'code', 'document no', 'codification document', 'codification'],
  label: ['titre', 'titres des documents', 'intitule', 'designation', 'libelle', 'document', 'livrable', 'nom document'],
  phase: ['phase', 'phase code', 'etape'],
  lot: ['lot', 'discipline', 'corps d etat', 'specialite', 'emetteur'],
  work: ['ouvrage', 'sequence ouvrage', 'sequence', 'station', 'batiment', 'site', 'sous station', 'sous station ssr'],
  level: ['niveau', 'zone', 'localisation', 'emplacement'],
  documentType: ['type icc', 'code icc', 'classification icc'],
  date: ['date', 'date remise', 'echeance', 'date livraison', 'date prevue', 'dates previsionnelles de diffusion', 'date previsionnelle de diffusion'],
  notes: ['notes', 'commentaire', 'remarque', 'observations'],
};
export const LDA_FIELDS = [
  ['reference', 'Référence client'], ['label', 'Intitulé / désignation'], ['phase', 'Phase'], ['lot', 'Lot / discipline'],
  ['work', 'Ouvrage / station'], ['level', 'Niveau / zone'], ['documentType', 'Type ICC'], ['date', 'Échéance'], ['notes', 'Notes'],
];
const aliasMatchScore = (header, alias) => {
  if (!header || !alias) return 0;
  if (header === alias) return 100;
  if (header.startsWith(`${alias} `) || header.endsWith(` ${alias}`)) return 85;
  if (header.includes(alias)) return alias.length >= 4 ? 70 : 0;
  return 0;
};
const bestFieldMatch = (header) => {
  let best = null;
  Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => aliases.forEach((alias) => {
    const score = aliasMatchScore(header, strip(alias));
    if (score > (best?.score || 0)) best = { field, score };
  }));
  return best;
};
export const detectLdaMapping = (headers = []) => {
  const normalized = headers.map((header) => strip(asText(header)));
  const mapping = Object.fromEntries(Object.keys(FIELD_ALIASES).map((field) => [field, '']));
  const usedColumns = new Set();
  normalized.forEach((header, index) => {
    const match = bestFieldMatch(header);
    if (!match || match.score < 70 || mapping[match.field] !== '') return;
    mapping[match.field] = index; usedColumns.add(index);
  });
  return mapping;
};
const headerRowScore = (row = []) => {
  const values = row.map((value) => strip(asText(value))).filter(Boolean);
  const distinct = [...new Set(values)];
  // A merged title such as "Liste des livrables" can be repeated by ExcelJS on
  // every merged cell. It must never be considered a header row.
  if (distinct.length < 2) return -100;
  const mapping = detectLdaMapping(row);
  const matchedFields = Object.entries(mapping).filter(([, index]) => index !== '');
  let score = matchedFields.length * 12;
  if (mapping.label !== '') score += 25;
  if (mapping.reference !== '') score += 12;
  if (mapping.lot !== '') score += 6;
  if (mapping.phase !== '') score += 6;
  if (mapping.work !== '') score += 5;
  // Prefer table-like rows with several different short labels and penalise
  // metadata/value rows (Indice / 01 / Origine...) that contain little structure.
  score += Math.min(distinct.length, 8);
  if (values.length > distinct.length * 2) score -= 20;
  return score;
};
const findHeaderRowIndex = (rows = []) => {
  let bestIndex = 0; let bestScore = -Infinity;
  rows.slice(0, 100).forEach((row, index) => {
    const score = headerRowScore(row);
    if (score > bestScore) { bestScore = score; bestIndex = index; }
  });
  return bestScore >= 25 ? bestIndex : 0;
};
const buildSelection = (rows, headerRowIndex) => {
  const headerRow = rows[headerRowIndex] || [];
  const followingRows = rows.slice(headerRowIndex + 1, headerRowIndex + 21);
  const columnCount = Math.max(headerRow.length, 1, ...followingRows.map((row) => row.length));
  const seen = new Map();
  const headers = Array.from({ length: columnCount }, (_, index) => {
    const raw = String(asText(headerRow[index]) || '').trim();
    const base = raw || `Colonne ${index + 1}`;
    const count = (seen.get(base) || 0) + 1; seen.set(base, count);
    return count > 1 ? `${base} (${count})` : base;
  });
  const mapping = detectLdaMapping(headers);
  let dataRows = rows.slice(headerRowIndex + 1).filter((row) => row.some((value) => String(asText(value) ?? '').trim()));
  // Many contractual LDAs contain section titles between deliverables. When a
  // real reference/codification column exists, those title rows have no
  // reference and must not be offered as deliverables.
  if (mapping.reference !== '') {
    dataRows = dataRows.filter((row) => String(asText(row[Number(mapping.reference)]) ?? '').trim());
  }
  return { headers, rows: dataRows, mapping, headerRowIndex };
};
const sheetScore = (rows = []) => {
  const headerRowIndex = findHeaderRowIndex(rows);
  const selection = buildSelection(rows, headerRowIndex);
  const mapping = selection.mapping || {};
  let score = headerRowScore(rows[headerRowIndex] || []);
  if (mapping.reference !== '') score += 35;
  if (mapping.label !== '') score += 45;
  if (mapping.phase !== '') score += 15;
  score += Math.min(selection.rows.length, 100) / 4;
  return { score, headerRowIndex, selection };
};
export const selectLdaHeaderRow = (parsed, headerRowIndex) => {
  const rawRows = parsed?.rawRows || [];
  const index = Math.max(0, Math.min(Number(headerRowIndex) || 0, Math.max(rawRows.length - 1, 0)));
  return { ...parsed, ...buildSelection(rawRows, index) };
};
export const selectLdaSheet = (parsed, sheetName) => {
  const sheet = (parsed?.sheets || []).find((entry) => entry.name === sheetName);
  if (!sheet) return parsed;
  const assessed = sheetScore(sheet.rawRows || []);
  return {
    ...parsed,
    selectedSheetName: sheet.name,
    rawRows: sheet.rawRows || [],
    ...assessed.selection,
  };
};
const worksheetRows = (sheet) => {
  const rows = [];
  sheet.eachRow({ includeEmpty: false }, (row) => {
    const values = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const isMergedFollower = cell.isMerged && cell.master && cell.address !== cell.master.address;
      values[colNumber - 1] = isMergedFollower ? '' : asText(cell.value);
    });
    // Keep the actual Excel row number so the UI's line number remains true.
    rows[row.number - 1] = values;
  });
  return Array.from({ length: rows.length }, (_, index) => rows[index] || []);
};
export async function parseLdaFile(file) {
  const name = String(file?.name || '').toLowerCase();
  let rows = [];
  let sheets = [];
  let selectedSheetName = '';
  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    const lines = String(await file.text()).replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
    if (!lines.length) throw new Error('Le fichier est vide.');
    const candidates = [';', '\t', ','];
    const delimiter = candidates.sort((a, b) => lines[0].split(b).length - lines[0].split(a).length)[0];
    rows = lines.map((line) => parseDelimitedLine(line, delimiter));
  } else if (name.endsWith('.xlsx')) {
    const module = await import('exceljs'); const ExcelJS = module.default || module;
    const workbook = new ExcelJS.Workbook(); await workbook.xlsx.load(await file.arrayBuffer());
    if (!workbook.worksheets.length) throw new Error('Le classeur Excel ne contient aucune feuille.');
    sheets = workbook.worksheets.map((sheet) => {
      const rawRows = worksheetRows(sheet);
      const assessed = sheetScore(rawRows);
      return { name: sheet.name, rawRows, score: assessed.score, headerRowIndex: assessed.headerRowIndex };
    });
    const best = [...sheets].sort((a, b) => b.score - a.score)[0];
    selectedSheetName = best.name;
    rows = best.rawRows;
  } else throw new Error('Format non pris en charge. Utilisez un fichier .xlsx ou .csv.');
  if (rows.length < 2) throw new Error('Le fichier ne contient aucune ligne de LDA exploitable.');
  const headerRowIndex = findHeaderRowIndex(rows);
  return { fileName: file.name, sheets, selectedSheetName, rawRows: rows, ...buildSelection(rows, headerRowIndex) };
}
const cell = (row, index) => index === '' || index == null ? '' : asText(row[Number(index)]);
const dateOnly = (value) => {
  value = asText(value); if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  if (typeof value === 'number' && value > 20000 && value < 100000) return new Date(Date.UTC(1899, 11, 30) + Math.round(value) * 86400000).toISOString().slice(0, 10);
  const raw = String(value).trim(); if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const match = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (match) { const year = Number(match[3]) < 100 ? 2000 + Number(match[3]) : Number(match[3]); return `${year}-${String(match[2]).padStart(2, '0')}-${String(match[1]).padStart(2, '0')}`; }
  return '';
};
const makeKey = () => `planning-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const LOT_ALIASES = {
  CFO: ['cfo', 'courants forts', 'courant fort', 'electricite', 'electricite cfo', 'energie', 'puissance'],
  CFA: ['cfa', 'courants faibles', 'courant faible', 'vdie', 'vdi', 'gtb', 'gti', 'surete', 'controle acces', 'videosurveillance'],
  SSI: ['ssi', 'securite incendie', 'incendie', 'sdai', 'smsi'],
  PV: ['pv', 'photovoltaique', 'photovoltaiques', 'solaire'],
  IRVE: ['irve', 'recharge vehicule electrique', 'recharge vehicules electriques', 'borne de recharge', 'bornes de recharge'],
  HTA: ['hta', 'haute tension', 'ht'],
  AUTOMATISME: ['automatisme', 'automatismes', 'controle commande', 'controle-commande', 'api', 'plc', 'scada'],
};
const PHASE_ALIASES = {
  CON: ['con', 'conception', 'etudes', 'etude', 'design', 'pro', 'dce'],
  EXE: ['exe', 'execution', 'etudes execution', 'etude execution', 'realisation', 'visa'],
  DOE: ['doe', 'dossier ouvrages executes', 'dossier des ouvrages executes', 'as built', 'recolement'],
};
const canonicalFromAliases = (value, aliases) => {
  const normalized = strip(value);
  if (!normalized) return { code: '', confidence: null, original: String(value || '').trim() };
  for (const [code, values] of Object.entries(aliases)) {
    const candidates = [strip(code), ...values.map(strip)];
    if (candidates.includes(normalized)) return { code, confidence: 'high', original: String(value || '').trim() };
  }
  for (const [code, values] of Object.entries(aliases)) {
    const candidates = [strip(code), ...values.map(strip)].filter((entry) => entry.length >= 3);
    if (candidates.some((entry) => normalized.includes(entry) || entry.includes(normalized))) return { code, confidence: 'medium', original: String(value || '').trim() };
  }
  return { code: String(value || '').trim().toUpperCase(), confidence: 'low', original: String(value || '').trim() };
};
export const normalizeImportedLot = (value) => canonicalFromAliases(value, LOT_ALIASES);
export const normalizeImportedPhase = (value) => canonicalFromAliases(value, PHASE_ALIASES);

const nodeIdentity = (node) => String(node?.sourceAffairNodeId || node?.id || node?.key || '');
const nodeParentIdentity = (node) => String(node?.parentSourceAffairNodeId || node?.parentId || node?.parent_id || node?.parentKey || '');
const matchScore = (query, candidate) => {
  const q = strip(query); const c = strip(candidate);
  if (!q || !c) return 0;
  if (q === c) return 100;
  if ((c.includes(q) || q.includes(c)) && Math.min(q.length, c.length) >= 4) return 85;
  const qTokens = new Set(q.split(' ').filter((token) => token.length > 1));
  const cTokens = new Set(c.split(' ').filter((token) => token.length > 1));
  if (!qTokens.size || !cTokens.size) return 0;
  const common = [...qTokens].filter((token) => cTokens.has(token)).length;
  const ratio = common / Math.max(qTokens.size, cTokens.size);
  return ratio >= 0.75 ? 75 : ratio >= 0.5 && common >= 2 ? 60 : 0;
};
export const matchAffairNode = (value, nodes = [], allowedTypes = null) => {
  const text = String(value || '').trim(); if (!text) return null;
  const candidates = (nodes || []).filter((node) => !allowedTypes || allowedTypes.includes(node.nodeType));
  const ranked = candidates.map((node) => ({ node, score: Math.max(matchScore(text, node.name), matchScore(text, node.code)) })).filter((entry) => entry.score > 0).sort((a, b) => b.score - a.score);
  if (!ranked.length || ranked[0].score < 75) return null;
  // Do not auto-link an ambiguous fuzzy match. Exact matches remain deterministic.
  if (ranked[0].score < 100 && ranked[1] && ranked[1].score === ranked[0].score) return null;
  return { node: ranked[0].node, confidence: ranked[0].score >= 100 ? 'high' : ranked[0].score >= 85 ? 'medium' : 'low', score: ranked[0].score };
};
const descendantsOf = (parent, nodes = []) => {
  if (!parent) return nodes;

  const ids = new Set([nodeIdentity(parent)]);
  let changed = true;

  while (changed) {
    changed = false;

    for (const node of nodes || []) {
      if (
        !ids.has(nodeIdentity(node)) &&
        ids.has(nodeParentIdentity(node))
      ) {
        ids.add(nodeIdentity(node));
        changed = true;
      }
    }
  }

  return (nodes || []).filter(
    (node) =>
      ids.has(nodeIdentity(node)) &&
      nodeIdentity(node) !== nodeIdentity(parent)
  );
};
const PHASE_RANK = { CON: 1, EXE: 2, DOE: 3 };
const importedSection = (item) => ({
  workId: item?.importMetadata?.matchedAffairNodeId || null,
  levelId: item?.importMetadata?.matchedLevelNodeId || null,
  rawWork: normalizeText(item?.importMetadata?.originalWork || ''),
  rawLevel: normalizeText(item?.importMetadata?.originalLevel || ''),
});
const importedSectionCompatible = (item, candidate) => {
  const target = importedSection(item); const parent = importedSection(candidate);
  // A project/common document can feed any section, but a scoped document can
  // never feed another station/ouvrage.
  if (!parent.workId && !parent.rawWork) return true;
  if (parent.workId) {
    if (!target.workId || target.workId !== parent.workId) return false;
  } else if (!target.rawWork || target.rawWork !== parent.rawWork) return false;
  // A station-wide predecessor may feed one of its levels. A level-scoped
  // predecessor must stay inside that exact level.
  if (parent.levelId && target.levelId !== parent.levelId) return false;
  if (!parent.levelId && parent.rawLevel && target.rawLevel !== parent.rawLevel) return false;
  return true;
};
export function applyImportedLdaDependencies(items = [], catalog = {}) {
  const byCatalogCode = new Map((catalog.deliverables || []).map((doc) => [String(doc.code || '').toUpperCase(), doc]));
  const byDocumentCode = new Map();
  items.forEach((item) => {
    const code = String(item.documentTypeCode || '').toUpperCase(); if (!code) return;
    const list = byDocumentCode.get(code) || []; list.push(item); byDocumentCode.set(code, list);
  });
  const scoreCandidate = (item, candidate) => {
    if (!candidate || candidate.clientKey === item.clientKey || !importedSectionCompatible(item, candidate)) return -1;
    if ((PHASE_RANK[candidate.phaseCode] || 0) > (PHASE_RANK[item.phaseCode] || 0)) return -1;
    const target = importedSection(item); const parent = importedSection(candidate);
    let score = 0;
    if (target.workId && parent.workId === target.workId) score += 100;
    else if (!parent.workId) score += 20;
    if (target.levelId && parent.levelId === target.levelId) score += 60;
    else if (!parent.levelId) score += 10;
    if (item.phaseCode && candidate.phaseCode === item.phaseCode) score += 24;
    if (item.lotCode && candidate.lotCode === item.lotCode) score += 16;
    return score;
  };
  items.forEach((item) => {
    const definition = byCatalogCode.get(String(item.documentTypeCode || '').toUpperCase());
    const dependsOn = Array.isArray(definition?.dependsOn) ? definition.dependsOn : [];
    const resolved = [];
    dependsOn.forEach((dependencyCode) => {
      let best = null; let bestScore = -1; let ambiguous = false;
      (byDocumentCode.get(String(dependencyCode).toUpperCase()) || []).forEach((candidate) => {
        const score = scoreCandidate(item, candidate);
        if (score > bestScore) { best = candidate; bestScore = score; ambiguous = false; }
        else if (score >= 0 && score === bestScore) ambiguous = true;
      });
      if (bestScore >= 0 && !ambiguous && best?.clientKey) resolved.push(best.clientKey);
    });
    item.dependencyIds = [...new Set(resolved)];
    item.importMetadata = { ...(item.importMetadata || {}), automaticDependencies: item.dependencyIds.length };
  });
  return items;
}

export function buildLdaItems({ rows = [], mapping = {}, affairNodes = [], catalog = {} }) {
  if (mapping.label === '' || mapping.label == null) throw new Error('Sélectionnez la colonne contenant l’intitulé du livrable.');
  const nodes = affairNodes || [];
  const deliverables = catalog.deliverables || [];
  const byDocCode = new Map(deliverables.map((doc) => [String(doc.code || '').toUpperCase(), doc]));
  const workTypes = ['subproject', 'site', 'building', 'station', 'other'];
  const items = rows.map((row) => {
    const label = String(cell(row, mapping.label) || '').trim(); if (!label) return null;
    const reference = String(cell(row, mapping.reference) || '').trim();
    const workText = String(cell(row, mapping.work) || '').trim();
    const levelText = String(cell(row, mapping.level) || '').trim();
    const workMatch = matchAffairNode(workText, nodes, workTypes);
    const matchedNode = workMatch?.node || null;
    const levelCandidates = matchedNode ? descendantsOf(matchedNode, nodes) : nodes;
    const levelMatch = matchAffairNode(levelText, levelCandidates, ['level', 'zone']);
    const matchedLevel = levelMatch?.node || null;
    const lotMatch = normalizeImportedLot(cell(row, mapping.lot));
    const phaseMatch = normalizeImportedPhase(cell(row, mapping.phase));
    let documentTypeCode = String(cell(row, mapping.documentType) || '').trim().toUpperCase();
    if (documentTypeCode && !byDocCode.has(documentTypeCode)) documentTypeCode = '';
    const automaticClassification = documentTypeCode ? null : classifyLdaDeliverable({
      label, reference, notes: String(cell(row, mapping.notes) || '').trim(), catalog,
    });
    if (!documentTypeCode && automaticClassification?.code) documentTypeCode = automaticClassification.code;
    const resolvedWork = matchedNode ? String(matchedNode.name || workText).toUpperCase() : workText.toUpperCase();
    const resolvedLevel = matchedLevel ? String(matchedLevel.name || levelText).toUpperCase() : levelText.toUpperCase();
    return {
      clientKey: makeKey(), itemType: 'work', source: 'lda_client', title: label.slice(0, 255),
      clientReference: reference, clientLabel: label,
      phaseCode: phaseMatch.code,
      lotCode: lotMatch.code,
      levelCode: resolvedLevel,
      scopeType: matchedNode ? ((matchedLevel || levelText) ? 'WORK_LEVEL' : 'WORK') : (levelText ? 'LEVEL' : ''),
      scopeCode: matchedNode ? resolvedWork : (workText || levelText).toUpperCase(),
      documentTypeCode, startDate: '', endDate: dateOnly(cell(row, mapping.date)), estimatedHours: '',
      dependencyIds: [], clientConstraintKey: null, phaseParentKey: null, isClientConstraint: false, isLocked: false,
      notes: String(cell(row, mapping.notes) || '').trim(), autoEstimatedHours: null, estimation: null, estimationConfidence: null,
      assignedResourceId: null, assignedResourceName: null, assignedResourceManual: false, resourcePool: null,
      importMetadata: {
        sourceFile: null,
        originalWork: workText || null,
        originalLevel: levelText || null,
        originalLot: lotMatch.original || null,
        originalPhase: phaseMatch.original || null,
        matchedAffairNodeId: matchedNode ? nodeIdentity(matchedNode) : null,
        matchedLevelNodeId: matchedLevel ? nodeIdentity(matchedLevel) : null,
        automaticWorkMatch: workMatch ? { confidence: workMatch.confidence, score: workMatch.score } : null,
        automaticLevelMatch: levelMatch ? { confidence: levelMatch.confidence, score: levelMatch.score } : null,
        automaticLotMatch: lotMatch.confidence ? { code: lotMatch.code, confidence: lotMatch.confidence } : null,
        automaticPhaseMatch: phaseMatch.confidence ? { code: phaseMatch.code, confidence: phaseMatch.confidence } : null,
        automaticClassification,
      },
    };
  }).filter(Boolean);
  return applyImportedLdaDependencies(items, catalog);
}

